import { CreateS3Folder } from '../../../services/command/aiPrompt/S3BrowserCommand.services';

export type RetouchStatusSeverity = 'success' | 'error' | 'warning' | 'info';

export type RetouchBatchSaveResult = {
    successCount: number;
    errorCount: number;
    processedCount: number;
    errors: string[];
    requestId?: number;
};

export type RetouchTransferStatus = {
    severity: RetouchStatusSeverity;
    message: string;
    details: string[];
};

export const resolveTransferStatus = (
    completedUploadCount: number,
    failedUploadCount: number,
    batchResult: RetouchBatchSaveResult | undefined,
    t: (key: string, options?: Record<string, unknown>) => string
): RetouchTransferStatus => {
    if (completedUploadCount === 0) {
        return {
            severity: 'error',
            message: t('studio.upload.allUploadsFailed'),
            details: [],
        };
    }

    if (!batchResult) {
        if (failedUploadCount > 0) {
            return {
                severity: 'warning',
                message: t('studio.retouch.uploadPartial', {
                    success: completedUploadCount,
                    failed: failedUploadCount,
                }),
                details: [],
            };
        }
        return {
            severity: 'success',
            message: t('studio.upload.allUploadsCompleted'),
            details: [],
        };
    }

    if (batchResult.errorCount === 0 && batchResult.successCount > 0) {
        return {
            severity: failedUploadCount > 0 ? 'warning' : 'success',
            message: t('studio.retouch.batchSaveSuccess', {
                count: batchResult.successCount,
                requestId: batchResult.requestId ?? '-',
            }),
            details: [],
        };
    }

    if (batchResult.successCount > 0 && batchResult.errorCount > 0) {
        return {
            severity: 'warning',
            message: t('studio.retouch.batchSavePartial', {
                success: batchResult.successCount,
                failed: batchResult.errorCount,
            }),
            details: batchResult.errors,
        };
    }

    return {
        severity: 'error',
        message: t('studio.retouch.batchSaveFailed'),
        details: batchResult.errors,
    };
};

export const parseRetouchBatchResult = (
    message: string
): RetouchBatchSaveResult | undefined => {
    if (!message) return undefined;
    try {
        const parsed = JSON.parse(message);
        if (typeof parsed !== 'object' || parsed === null) return undefined;
        return {
            successCount: Number(parsed.successCount ?? parsed.SuccessCount ?? 0),
            errorCount: Number(parsed.errorCount ?? parsed.ErrorCount ?? 0),
            processedCount: Number(parsed.processedCount ?? parsed.ProcessedCount ?? 0),
            errors: Array.isArray(parsed.errors ?? parsed.Errors) ? (parsed.errors ?? parsed.Errors) : [],
            requestId: parsed.requestId ?? parsed.RequestId,
        };
    } catch {
        return undefined;
    }
};

export const shouldShowTransferButton = (
    transferUploadItems: { status: string }[],
    isUploading: boolean,
    pendingStatus: string,
    completedStatus: string,
    failedStatus: string
): boolean => {
    if (transferUploadItems.length === 0 || isUploading) return true;
    const allTerminal = transferUploadItems.every(
        (i) => i.status === completedStatus || i.status === failedStatus
    );
    const transferStarted = transferUploadItems.some((i) => i.status !== pendingStatus);
    return !(allTerminal && transferStarted);
};

export const ensureRetouchS3Folder = async (uploadBasePrefix: string): Promise<void> => {
    const cleanPrefix = uploadBasePrefix.replace(/\/$/, '');
    const lastSlashIndex = cleanPrefix.lastIndexOf('/');
    if (lastSlashIndex < 0) {
        return;
    }

    const parentPrefix = cleanPrefix.substring(0, lastSlashIndex + 1);
    const folderName = cleanPrefix.substring(lastSlashIndex + 1);

    try {
        await CreateS3Folder({
            prefix: parentPrefix,
            name: folderName,
        });
        console.log('Pre-created S3 folder for Retouch:', cleanPrefix);
    } catch (e) {
        console.error('Failed to pre-create S3 folder for Retouch', e);
    }
};
